import React, { useCallback, useEffect, useMemo, useState } from "react"
import { Modal } from "react-bootstrap"

import PeriodPicker from "pages/parts/PeriodPicker"
import { GET, httpOk } from "helpers/api"
import { formatDate, formatMoney } from "helpers/helpers"

/**
 * Подключения партнёров.
 *
 * Период фильтрует ТОЛЬКО дату подключения точки, а оплата ищется за всё время:
 * партнёр подключает в августе, тестовый период заканчивается 1 сентября, и до
 * 5 сентября точка должна заплатить. Если бы оплату резали тем же периодом,
 * августовский срез всегда показывал бы нули.
 *
 * Экран общий для админки и партнёрского кабинета: разница только в ручках.
 * Кого куда пускать, решает бэк — партнёру отчёт приходит уже урезанным до его
 * ветки (свои подключения и подключения подчинённых).
 */

const cardStyle = {
	border: "1px solid var(--dark-border, #edf0f4)",
	borderRadius: "12px",
	boxShadow: "0 4px 14px rgba(15, 23, 42, 0.04)",
}

const mutedStyle = {
	color: "var(--dark-text-muted, #8791a1)",
	fontSize: "12px",
}

const STATUS_LABELS = {
	1: { label: "Работает", color: "var(--dark-fg-success, #1f9d57)" },
	"-1": { label: "Отключена", color: "var(--dark-fg-danger, #d23434)" },
	"-2": { label: "Заморожена", color: "#f0a04c" },
}

// Статусы начисления абонентки (PosBilling::CHARGE_*)
const CHARGE_STATUS_LABELS = {
	1: { label: "Не оплачено", color: "var(--dark-fg-danger, #d23434)" },
	2: { label: "Оплачено частично", color: "#f0a04c" },
	3: { label: "Оплачено", color: "var(--dark-fg-success, #1f9d57)" },
	4: { label: "Отменено", color: "var(--dark-text-muted, #8791a1)" },
}

// Состояние оплаты абонентки. «Бесплатно» — это и выданный бесплатный период,
// и тестовый до первого счёта: платить в обоих случаях пока не за что.
const PAYMENT_STATES = {
	paid: { label: "оплачена", color: "var(--dark-fg-success, #1f9d57)", bucket: "paid" },
	free: { label: "бесплатный период", color: "#4c8df0", bucket: "free" },
	pending: { label: "срок не наступил", color: "#4c8df0", bucket: "free" },
	credit: { label: "кредит", color: "#f0a04c", bucket: "credit" },
	unpaid: { label: "не оплачена", color: "var(--dark-fg-danger, #d23434)", bucket: "unpaid" },
}

const MODAL_TITLES = {
	all: "Все подключения",
	paid: "Оплатили абонентку",
	unpaid: "Не оплатили абонентку",
	free: "Бесплатный период",
	credit: "Кредит на оплату",
}

const GRANT_STATUS_LABELS = {
	1: { label: "Действует", color: "var(--dark-fg-success, #1f9d57)" },
	2: { label: "Истёк", color: "var(--dark-text-muted, #8791a1)" },
	3: { label: "Отозван", color: "var(--dark-text-muted, #8791a1)" },
}

function monthRange(date) {
	return {
		start_date: formatDate(new Date(date.getFullYear(), date.getMonth(), 1), "yyyy-MM-dd"),
		end_date: formatDate(new Date(date.getFullYear(), date.getMonth() + 1, 0), "yyyy-MM-dd"),
	}
}

/** Цифра-ссылка в таблице по партнёрам: открывает список тех самых точек. */
function CountLink({ value, color, onClick }) {
	if (!value) return <span style={{ color: color || "inherit" }}>0</span>

	return (
		<span role="button" onClick={onClick}
			style={{ color: color || "#4c8df0", textDecoration: "underline", cursor: "pointer" }}>
			{value}
		</span>
	)
}

/**
 * Пороги конверсии. Норма — что платят почти все: подключение, которое не дошло
 * до оплаты, это не «сложный клиент», а зря потраченный месяц. Поэтому зелёным
 * считается только 80% и выше, а всё, что ниже двух третей, — красное.
 */
const CONVERSION_THRESHOLDS = { green: 80, yellow: 60 }

function conversionColor(value) {
	if (value === null || value === undefined) return "#8791a1"
	if (value >= CONVERSION_THRESHOLDS.green) return "#1f9d57"
	if (value >= CONVERSION_THRESHOLDS.yellow) return "#f0a04c"
	return "#d23434"
}

/** Конверсия с цветом порога. Платить ещё никто не обязан — показываем прочерк. */
function Conversion({ value, bold }) {
	if (value === null || value === undefined) {
		return <span style={{ color: "var(--dark-text-muted, #8791a1)" }}>—</span>
	}

	return (
		<span style={{ color: conversionColor(value), fontWeight: bold ? 700 : 500 }}>
			{value} %
		</span>
	)
}

/** Состояние абонентки одной точки: оплачена, бесплатный период, кредит, долг. */
function PaymentState({ item }) {
	const state = PAYMENT_STATES[item.payment_state] || PAYMENT_STATES.unpaid
	let detail = ""

	if (item.payment_state === "paid" && item.first_paid_at) {
		detail = formatDate(item.first_paid_at, "dd.MM.yyyy")
	}
	if (item.payment_state === "free" && item.free_until) {
		detail = `до ${formatDate(item.free_until, "dd.MM.yyyy")}`
	}
	if (item.payment_state === "credit" && item.credit_until) {
		detail = `до ${formatDate(item.credit_until, "dd.MM.yyyy")}`
	}

	return (
		<span style={{ color: state.color }}>
			{state.label}{detail && ` ${detail}`}
		</span>
	)
}

/**
 * Дата последнего чека: точка не платит, но торгует — или уже бросила.
 * Больше недели без чеков подсвечиваем красным.
 */
function LastCheque({ value }) {
	if (!value) return <span style={{ color: "var(--dark-text-muted, #8791a1)" }}>чеков нет</span>

	const days = Math.floor((Date.now() - new Date(value).getTime()) / 86400000)
	const color = days > 7 ? "var(--dark-fg-danger, #d23434)" : "inherit"

	return (
		<span style={{ color }} title={formatDate(value, "dd.MM.yyyy HH:mm")}>
			{formatDate(value, "dd.MM.yyyy")}
			{days > 0 && <span style={mutedStyle}> · {days} дн.</span>}
		</span>
	)
}

// Подпись держим в одну строку: восемь карточек с переносами разъезжаются по
// высоте и съедают экран до таблиц. Полный текст — в подсказке по наведению.
const kpiSublabelStyle = {
	...mutedStyle,
	marginTop: "2px",
	whiteSpace: "nowrap",
	overflow: "hidden",
	textOverflow: "ellipsis",
}

function KpiCard({ title, value, sublabel, accent = "#111827" }) {
	return (
		<div className="card h-100 mb-0" style={cardStyle}>
			<div className="card-body py-2 px-3">
				<div style={{ ...mutedStyle, whiteSpace: "nowrap" }}>{title}</div>
				<h3 className="mb-0 mt-1"
					style={{ fontSize: "20px", fontWeight: 800, color: accent, letterSpacing: "-0.5px" }}>
					{value}
				</h3>
				{sublabel && <div style={kpiSublabelStyle} title={sublabel}>{sublabel}</div>}
			</div>
		</div>
	)
}

function Report({
	url = "/admin/partner-connections",
	billingUrl = "/admin/partner-connections/billing",
	title = "Подключения партнёров",
}) {
	// Черновик фильтра: даты, поиск и «оплатили/не оплатили» правятся свободно,
	// запрос уходит только по «Применить». Иначе выбор периода из двух дат сам по
	// себе давал два лишних запроса — и первый по заведомо чужому диапазону.
	const [filterData, setFilterData] = useState(monthRange(new Date()))
	const [appliedFilter, setAppliedFilter] = useState(monthRange(new Date()))
	const [data, setData] = useState({ data: [], partners: [], summary: null })
	const [loading, setLoading] = useState(false)
	const [modal, setModal] = useState({ show: false, partner: null, kind: "all", rows: [] })
	const [detail, setDetail] = useState({ posId: null, data: null, loading: false })

	const getData = useCallback(async (nextFilterData) => {
		const params = {
			start_date: nextFilterData.start_date,
			end_date: nextFilterData.end_date,
		}
		if (nextFilterData.search) params.search = nextFilterData.search
		if (nextFilterData.payment_status) params.payment_status = nextFilterData.payment_status

		setLoading(true)
		const response = await GET(url, params, { loader: false })
		setLoading(false)

		setAppliedFilter(nextFilterData)
		if (httpOk(response)) setData(response.data)
	}, [url])

	// Стрелки листают целыми месяцами от начала текущего периода: руками выставлять
	// две даты, чтобы просто посмотреть прошлый месяц, — самое частое действие в отчёте.
	// Один клик — один запрос, «Применить» после них нажимать не нужно.
	function shiftMonth(step) {
		const start = new Date(filterData.start_date)
		const next = { ...filterData, ...monthRange(new Date(start.getFullYear(), start.getMonth() + step, 1)) }
		setFilterData(next)
		getData(next)
	}

	// Список для модалки собираем из уже загруженных строк — за теми же точками
	// второй раз на сервер ходить незачем.
	function openModal(partner, kind) {
		const rows = data.data.filter((item) => {
			if ((item.partner_login || "") !== (partner.partner_login || "")) return false
			if (kind === "all") return true
			return PAYMENT_STATES[item.payment_state]?.bucket === kind
		})

		setModal({ show: true, partner, kind, rows })
		setDetail({ posId: null, data: null, loading: false })
	}

	async function toggleDetail(posId) {
		if (detail.posId === posId) {
			setDetail({ posId: null, data: null, loading: false })
			return
		}

		setDetail({ posId, data: null, loading: true })
		const response = await GET(billingUrl, { pos_id: posId }, { loader: false })
		setDetail({ posId, data: httpOk(response) ? response.data : null, loading: false })
	}

	const summary = data.summary || {}

	// «Всего» считаем по тем же строкам, что лежат в таблице, а не по сводке
	// сверху: строки режутся поиском и фильтром, и итог столбца обязан сходиться
	// с тем, что видно глазами. Конверсия — как на сервере: база это все
	// подключения, кроме бесплатных (кредит — отсрочка, платить всё равно надо).
	const partnersTotal = useMemo(() => {
		const total = data.partners.reduce((acc, item) => ({
			connections: acc.connections + (item.connections || 0),
			paid: acc.paid + (item.paid || 0),
			unpaid: acc.unpaid + (item.unpaid || 0),
			free: acc.free + (item.free || 0),
			credit: acc.credit + (item.credit || 0),
			paid_amount: acc.paid_amount + (Number(item.paid_amount) || 0),
		}), { connections: 0, paid: 0, unpaid: 0, free: 0, credit: 0, paid_amount: 0 })

		const base = total.connections - total.free
		total.conversion = base > 0 ? Math.round((total.paid / base) * 1000) / 10 : null

		return total
	}, [data.partners])

	const connectionsTotal = useMemo(() => data.data.reduce((acc, item) => ({
		tariff: acc.tariff + (Number(item.tariff) || 0),
		paid_amount: acc.paid_amount + (Number(item.paid_amount) || 0),
	}), { tariff: 0, paid_amount: 0 }), [data.data])

	const periodLabel = `${formatDate(appliedFilter.start_date)} — ${formatDate(appliedFilter.end_date)}`

	useEffect(() => {
		getData(filterData)
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header d-flex justify-content-between">
				<div className="vertical-center">
					<h4>{title}</h4>
				</div>
			</div>

			<div className="card mb-3" style={cardStyle}>
				<div className="card-body py-3">
					<div className="d-flex flex-wrap align-items-end gap-2">
						<div className="btn-group">
							<button className="btn btn-outline-primary" title="Предыдущий месяц"
								onClick={() => shiftMonth(-1)}>
								<i className="uil uil-angle-left-b"></i>
							</button>
							<button className="btn btn-outline-primary" title="Следующий месяц"
								onClick={() => shiftMonth(1)}>
								<i className="uil uil-angle-right-b"></i>
							</button>
						</div>

						<div style={{ minWidth: 260 }}>
							<label style={mutedStyle}>Подключены</label>
							<PeriodPicker
								startDate={filterData.start_date}
								endDate={filterData.end_date}
								onChange={(period) => setFilterData({ ...filterData, ...period })}
								withTime={false}
							/>
						</div>

						<div>
							<label style={mutedStyle}>Поиск</label>
							<input type="text" className="form-control" placeholder="Логин или название"
								value={filterData.search || ""}
								onChange={(event) => setFilterData({ ...filterData, search: event.target.value })}
								onKeyDown={(event) => {
									if (event.key === "Enter") getData(filterData)
								}} />
						</div>

						<div className="btn-group">
							<button className={`btn btn-outline-primary ${!filterData.payment_status ? "active" : ""}`}
								onClick={() => setFilterData({ ...filterData, payment_status: "" })}>
								Все
							</button>
							<button className={`btn btn-outline-primary ${filterData.payment_status === "paid" ? "active" : ""}`}
								onClick={() => setFilterData({ ...filterData, payment_status: "paid" })}>
								Оплатили
							</button>
							<button className={`btn btn-outline-primary ${filterData.payment_status === "unpaid" ? "active" : ""}`}
								onClick={() => setFilterData({ ...filterData, payment_status: "unpaid" })}>
								Не оплатили
							</button>
							<button className={`btn btn-outline-primary ${filterData.payment_status === "free" ? "active" : ""}`}
								onClick={() => setFilterData({ ...filterData, payment_status: "free" })}>
								Бесплатно
							</button>
							<button className={`btn btn-outline-primary ${filterData.payment_status === "credit" ? "active" : ""}`}
								onClick={() => setFilterData({ ...filterData, payment_status: "credit" })}>
								Кредит
							</button>
						</div>

						<button className="btn btn-primary btn-wide" onClick={() => getData(filterData)}>
							Применить
						</button>
					</div>

					<div style={{ ...mutedStyle, marginTop: "8px" }}>
						Период — дата подключения ({periodLabel}). Оплата считается за всё время:
						подключение августа, оплаченное в сентябре, попадает в «оплатили».
						Точки на бесплатном периоде и те, кому счёт ещё не выставлялся, в «не оплатили»
						не идут и в конверсию не считаются; кредит (доверительный платёж) — это отсрочка,
						такая точка платить всё ещё обязана.
					</div>
				</div>
			</div>

			<div className="row g-2 mb-3">
				<div className="col-md-3">
					<KpiCard title="Подключений" value={formatMoney(summary.connections || 0)}
						sublabel="новых точек за период" />
				</div>
				<div className="col-md-3">
					<KpiCard title="Оплатили" value={formatMoney(summary.paid || 0)}
						sublabel="абонентка закрыта полностью" accent="#1f9d57" />
				</div>
				<div className="col-md-3">
					<KpiCard title="Не оплатили" value={formatMoney(summary.unpaid || 0)}
						sublabel="срок наступил, денег нет" accent="#d23434" />
				</div>
				<div className="col-md-3">
					<KpiCard title="Конверсия"
						value={summary.conversion === null || summary.conversion === undefined
							? "—"
							: `${summary.conversion} %`}
						sublabel={`из тех, кто уже обязан платить · зелёный от ${CONVERSION_THRESHOLDS.green} %, жёлтый от ${CONVERSION_THRESHOLDS.yellow} %`}
						accent={conversionColor(summary.conversion)} />
				</div>
				<div className="col-md-3">
					<KpiCard title="Бесплатно" value={formatMoney(summary.free || 0)}
						sublabel="бесплатный период или счёта ещё не было" accent="#4c8df0" />
				</div>
				<div className="col-md-3">
					<KpiCard title="Кредит" value={formatMoney(summary.credit || 0)}
						sublabel="доверительный платёж, оплата отложена" accent="#f0a04c" />
				</div>
				<div className="col-md-3">
					<KpiCard title="Сумма оплат" value={formatMoney(summary.paid_amount || 0)}
						sublabel="от новых подключений" accent="#9b5de5" />
				</div>
				<div className="col-md-3">
					<KpiCard title="Партнёров" value={formatMoney(summary.partners_count || 0)}
						sublabel={`подключали за период, работает точек: ${summary.active || 0}`} />
				</div>
			</div>

			<div className="card mb-3" style={cardStyle}>
				<div className="card-body">
					<h5 className="mb-3">По партнёрам</h5>
					{data.partners.length === 0 &&
						<div style={mutedStyle}>{loading ? "Загрузка…" : "Нет подключений за период"}</div>
					}
					{data.partners.length > 0 &&
						<div className="table-responsive table-fixed"
							style={{ height: "auto", maxHeight: "420px", overflow: "auto" }}>
							<table className="table table-bordered mb-0">
								<thead>
									<tr>
										<th style={{ width: "33px", minWidth: "33px" }} className="text-center">#</th>
										<th>Партнёр</th>
										<th style={{ width: "15%" }}>Логин</th>
										<th style={{ width: "9%" }} className="text-center">Подключений</th>
										<th style={{ width: "9%" }} className="text-center">Оплатили</th>
										<th style={{ width: "9%" }} className="text-center">Не оплатили</th>
										<th style={{ width: "9%" }} className="text-center">Бесплатно</th>
										<th style={{ width: "9%" }} className="text-center">Кредит</th>
										<th style={{ width: "9%" }} className="text-center">Конверсия</th>
										<th style={{ width: "12%" }} className="text-end">Сумма оплат</th>
									</tr>
								</thead>
								<tbody>
									{data.partners.map((item, index) => (
										<tr key={item.partner_login || index}>
											<td className="text-center">{index + 1}</td>
											<td>{item.partner_name || "Без партнёра"}</td>
											<td>{item.partner_login || "—"}</td>
											<td className="text-center">
												<CountLink value={item.connections}
													onClick={() => openModal(item, "all")} />
											</td>
											<td className="text-center">
												<CountLink value={item.paid} color="#1f9d57"
													onClick={() => openModal(item, "paid")} />
											</td>
											<td className="text-center">
												<CountLink value={item.unpaid} color="#d23434"
													onClick={() => openModal(item, "unpaid")} />
											</td>
											<td className="text-center">
												<CountLink value={item.free} color="#4c8df0"
													onClick={() => openModal(item, "free")} />
											</td>
											<td className="text-center">
												<CountLink value={item.credit} color="#f0a04c"
													onClick={() => openModal(item, "credit")} />
											</td>
											<td className="text-center">
												<Conversion value={item.conversion} bold />
											</td>
											<td className="text-end">{formatMoney(item.paid_amount)}</td>
										</tr>
									))}
								</tbody>
								<tfoot>
									<tr style={{ fontWeight: 700 }}>
										<td colSpan="3">Всего [{data.partners.length}]</td>
										<td className="text-center">{formatMoney(partnersTotal.connections)}</td>
										<td className="text-center" style={{ color: "var(--dark-fg-success, #1f9d57)" }}>
											{formatMoney(partnersTotal.paid)}
										</td>
										<td className="text-center" style={{ color: "var(--dark-fg-danger, #d23434)" }}>
											{formatMoney(partnersTotal.unpaid)}
										</td>
										<td className="text-center" style={{ color: "#4c8df0" }}>
											{formatMoney(partnersTotal.free)}
										</td>
										<td className="text-center" style={{ color: "#f0a04c" }}>
											{formatMoney(partnersTotal.credit)}
										</td>
										<td className="text-center">
											<Conversion value={partnersTotal.conversion} bold />
										</td>
										<td className="text-end">{formatMoney(partnersTotal.paid_amount)}</td>
									</tr>
								</tfoot>
							</table>
						</div>
					}
				</div>
			</div>

			<div className="card" style={cardStyle}>
				<div className="card-body">
					<h5 className="mb-3">Подключения [{data.data.length}]</h5>
					{data.data.length === 0 &&
						<div style={mutedStyle}>{loading ? "Загрузка…" : "Нет подключений за период"}</div>
					}
					{data.data.length > 0 &&
						<div className="table-responsive table-fixed"
							style={{ height: "auto", maxHeight: "560px", overflow: "auto" }}>
							<table className="table table-bordered mb-0">
								<thead>
									<tr>
										<th style={{ width: "33px", minWidth: "33px" }} className="text-center">#</th>
										<th style={{ width: "10%" }} className="text-center">Подключение</th>
										<th>Заведение</th>
										<th style={{ width: "12%" }}>Логин</th>
										<th style={{ width: "15%" }}>Партнёр</th>
										<th style={{ width: "9%" }} className="text-end">Тариф</th>
										<th style={{ width: "13%" }} className="text-center">Абонентка</th>
										<th style={{ width: "9%" }} className="text-end">Оплачено</th>
										<th style={{ width: "9%" }} className="text-center">Статус</th>
									</tr>
								</thead>
								<tbody>
									{data.data.map((item, index) => {
										const status = STATUS_LABELS[String(item.status)]

										return (
											<tr key={item.pos_id}>
												<td className="text-center">{index + 1}</td>
												<td className="text-center text-nowrap">
													{formatDate(item.created_at, "dd.MM.yyyy")}
												</td>
												<td className="td-ellipsis" title={item.pos_name}>{item.pos_name}</td>
												<td className="td-ellipsis">{item.login}</td>
												<td className="td-ellipsis" title={item.partner_name}>
													{item.partner_name || "—"}
												</td>
												<td className="text-end">{formatMoney(item.tariff)}</td>
												<td className="text-center text-nowrap">
													<PaymentState item={item} />
												</td>
												<td className="text-end">
													{item.paid_amount > 0 ? formatMoney(item.paid_amount) : "—"}
												</td>
												<td className="text-center" style={{ color: status?.color }}>
													{status?.label || "—"}
												</td>
											</tr>
										)
									})}
								</tbody>
								<tfoot>
									<tr style={{ fontWeight: 700 }}>
										<td colSpan="5">Всего [{data.data.length}]</td>
										<td className="text-end">{formatMoney(connectionsTotal.tariff)}</td>
										<td></td>
										<td className="text-end">{formatMoney(connectionsTotal.paid_amount)}</td>
										<td></td>
									</tr>
								</tfoot>
							</table>
						</div>
					}
				</div>
			</div>

			{/* MODAL */}
			<Modal show={modal.show} size="xl"
				onHide={() => setModal({ ...modal, show: false })}>
				<Modal.Header closeButton>
					<Modal.Title>
						{MODAL_TITLES[modal.kind]}
						{modal.partner && ` — ${modal.partner.partner_name || "Без партнёра"}`}
						{` [${modal.rows.length}]`}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div style={{ ...mutedStyle, marginBottom: "8px" }}>
						Оплатившим считается точка, у которой хотя бы одно начисление абонентки
						закрыто деньгами полностью. Нажмите на строку — покажу начисления, движения
						денег и выданные бесплатные периоды и кредиты.
					</div>

					<div className="table-responsive">
						<table className="table table-bordered mb-0">
							<thead>
								<tr>
									<th style={{ width: "33px", minWidth: "33px" }} className="text-center">#</th>
									<th style={{ width: "6%" }} className="text-center">ID</th>
									<th style={{ width: "10%" }} className="text-center">Подключение</th>
									<th>Заведение</th>
									<th style={{ width: "15%" }}>Логин</th>
									<th style={{ width: "12%" }} className="text-end">Тариф</th>
									<th style={{ width: "12%" }} className="text-center">Абонентка</th>
									<th style={{ width: "12%" }} className="text-end">Оплачено</th>
									<th style={{ width: "13%" }} className="text-center">Последний чек</th>
								</tr>
							</thead>
							<tbody>
								{modal.rows.map((item, index) => (
									<React.Fragment key={item.pos_id}>
										<tr role="button" style={{ cursor: "pointer" }}
											onClick={() => toggleDetail(item.pos_id)}>
											<td className="text-center">{index + 1}</td>
											<td className="text-center">{item.pos_id}</td>
											<td className="text-center text-nowrap">
												{formatDate(item.created_at, "dd.MM.yyyy")}
											</td>
											<td className="td-ellipsis" title={item.pos_name}>{item.pos_name}</td>
											<td className="td-ellipsis">{item.login}</td>
											<td className="text-end">{formatMoney(item.tariff)}</td>
											<td className="text-center text-nowrap">
												<PaymentState item={item} />
											</td>
											<td className="text-end">
												{item.paid_amount > 0 ? formatMoney(item.paid_amount) : "—"}
											</td>
											<td className="text-center text-nowrap">
												<LastCheque value={item.last_cheque_at} />
											</td>
										</tr>

										{detail.posId === item.pos_id &&
											<tr>
												<td colSpan="9" style={{ background: "var(--dark-surface-2, #fbfcfe)" }}>
													{detail.loading && <div style={mutedStyle}>Загрузка…</div>}
													{!detail.loading && !detail.data &&
														<div style={mutedStyle}>Не удалось загрузить детализацию</div>
													}
													{!detail.loading && detail.data &&
														<BillingDetail detail={detail.data} />
													}
												</td>
											</tr>
										}
									</React.Fragment>
								))}
							</tbody>
						</table>
					</div>
				</Modal.Body>
				<Modal.Footer>
					<button className="btn btn-secondary" onClick={() => setModal({ ...modal, show: false })}>
						Закрыть
					</button>
				</Modal.Footer>
			</Modal>
		</>
	)
}

/** Начисления абонентки и движения денег по одной точке. */
function BillingDetail({ detail }) {
	const charges = detail.charges || []
	const history = detail.history || []
	const grants = detail.grants || []
	const pos = detail.pos || {}

	return (
		<div className="row g-3">
			<div className="col-12">
				<div style={mutedStyle}>
					Баланс: <b>{formatMoney(pos.balance || 0)}</b>
					{pos.paid_until_at && <> · оплачено по <b>{formatDate(pos.paid_until_at)}</b></>}
					{pos.billing_free_until && <> · бесплатный период до <b>{formatDate(pos.billing_free_until)}</b></>}
				</div>
			</div>

			<div className="col-md-6">
				<div style={{ ...mutedStyle, marginBottom: "4px" }}>Начисления</div>
				{charges.length === 0 && <div style={mutedStyle}>Начислений нет</div>}
				{charges.length > 0 &&
					<table className="table table-sm table-bordered mb-0" style={{ fontSize: "12px" }}>
						<thead>
							<tr>
								<th>Период</th>
								<th>За что</th>
								<th className="text-end">Начислено</th>
								<th className="text-end">Погашено</th>
								<th className="text-center">Статус</th>
							</tr>
						</thead>
						<tbody>
							{charges.map((item) => {
								const status = CHARGE_STATUS_LABELS[String(item.status)]

								return (
									<tr key={item.id}>
										<td className="text-nowrap">
											{formatDate(item.period_start, "dd.MM.yy")} — {formatDate(item.period_end, "dd.MM.yy")}
										</td>
										<td>{item.service_name || item.charge_kind_name}</td>
										<td className="text-end">{formatMoney(item.amount_final)}</td>
										<td className="text-end">{formatMoney(item.amount_allocated)}</td>
										<td className="text-center" style={{ color: status?.color }}>
											{status?.label || item.status}
										</td>
									</tr>
								)
							})}
						</tbody>
					</table>
				}
			</div>

			<div className="col-md-6">
				<div style={{ ...mutedStyle, marginBottom: "4px" }}>Движения денег</div>
				{history.length === 0 && <div style={mutedStyle}>Движений нет</div>}
				{history.length > 0 &&
					<table className="table table-sm table-bordered mb-0" style={{ fontSize: "12px" }}>
						<thead>
							<tr>
								<th>Дата</th>
								<th>Операция</th>
								<th>Способ</th>
								<th className="text-end">Сумма</th>
							</tr>
						</thead>
						<tbody>
							{history.map((item) => {
								const isMinus = item.transaction_type === "minus"

								return (
									<tr key={item.id}>
										<td className="text-nowrap">{formatDate(item.created_at, "dd.MM.yy HH:mm")}</td>
										<td>{item.note || (isMinus ? "Списание" : "Пополнение")}</td>
										<td>{item.payment_type_name || item.payment_type || "—"}</td>
										<td className={`text-end ${isMinus ? "text-danger" : "text-success"}`}>
											{isMinus ? "−" : "+"}{formatMoney(item.amount)}
										</td>
									</tr>
								)
							})}
						</tbody>
					</table>
				}
			</div>

			<div className="col-12">
				<div style={{ ...mutedStyle, marginBottom: "4px" }}>Бесплатные периоды и кредиты</div>
				{grants.length === 0 && <div style={mutedStyle}>Не выдавались</div>}
				{grants.length > 0 &&
					<table className="table table-sm table-bordered mb-0" style={{ fontSize: "12px" }}>
						<thead>
							<tr>
								<th style={{ width: "20%" }}>Что выдано</th>
								<th style={{ width: "22%" }}>Срок</th>
								<th style={{ width: "13%" }} className="text-center">Статус</th>
								<th>Причина</th>
								<th style={{ width: "15%" }}>Кто выдал</th>
							</tr>
						</thead>
						<tbody>
							{grants.map((item) => {
								const status = GRANT_STATUS_LABELS[String(item.status)]

								return (
									<tr key={item.id}>
										<td>{item.grant_type_name}</td>
										<td className="text-nowrap">
											{formatDate(item.granted_from, "dd.MM.yy")} — {formatDate(item.granted_to, "dd.MM.yy")}
										</td>
										<td className="text-center" style={{ color: status?.color }}>
											{status?.label || item.status}
										</td>
										<td>{item.reason || "—"}</td>
										<td>{item.created_by_name || "—"}</td>
									</tr>
								)
							})}
						</tbody>
					</table>
				}
			</div>
		</div>
	)
}

export default Report
